// 视频 tab —— 手机模式首页：门店列表 + inline 设备 chip + 搜索/全部/收藏/筛选
// 复刻 Android 视频 tab（参考截图 1）
import { Alert, Button, Card, Drawer, Input, Tabs, Empty, Spin, Tag, Space, Typography, message as antdMessage } from 'antd';
import {
	AppstoreOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DownOutlined,
	FilterOutlined,
	LeftOutlined,
	PhoneOutlined,
	RightOutlined,
	SearchOutlined,
	ShopOutlined,
	StarFilled,
	StarOutlined,
	VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { callInternal } from '../api/internal.js';
import {
	fetchApacCollectedStoreIds,
	fetchApacStoreIdsByChannelStatus,
	fetchApacStoreDataMetricValues,
	setApacStoreCollected,
	type ApacStoreDataMetricKey,
} from '../api/apac-store.js';
import { fetchFastChannels, fastChannelToDevice, type DeviceLite } from '../api/fast-device.js';
import { deviceLabel } from '../utils/device-label.js';
import { normalizeLocale } from '../utils/locale-format.js';
import { useGlobalStore } from '../stores/global-store.js';
import { capabilitiesForArea } from '../capabilities.js';
import { useRegion } from '../hooks/useRegion.js';
import {
	ActionTileGrid,
	MobileRootPage,
	MOBILE_ROOT_PRIMARY,
	RegionTag,
	RootHeader,
	SectionBlock,
	SummaryStrip,
} from '../components/MobileRootScaffold.js';
import { mobileColor, mobileFontSize, mobileFontWeight, mobileRadius, mobileShadow } from '../theme/mobile-typography.js';

interface Store {
	store_id: string;
	store: string;
	store_code: string;
	addr: string;
	business_hours: string;
	has_device: number;
	store_status: number;
	create_time?: string | number;
	parent_id?: string;
	branch_code?: string;
	group_id?: string;
	group_name?: string;
	store_phone?: string;
	shopowner_phone?: string;
	more_phone?: string[] | string;
	is_collected?: string | number;
	sort?: string | number;
}

interface StoreGroup {
	id?: string;
	group_id?: string;
	name?: string;
	group_name?: string;
	store_count?: number;
	[k: string]: unknown;
}

interface StoreVideoRow {
	store: Store;
	index: number;
	stats: StoreDeviceStats;
	devices: DeviceLite[];
}

interface StoreDeviceStats {
	total: number;
	online: number;
	offline: number;
}

type DeviceFilter = 'any' | 'has' | 'none';
type StatusFilter = 'any' | 'online' | 'offline';
type FavFilter    = 'any' | 'fav' | 'unfav';
type SortKey      = 'create' | 'name';
type ApacSortKey  = 'default' | 'create_desc' | 'name_az';
type ApacStoreTab = 'video' | 'data';
type StorePageMode = 'normal' | 'favorite';
type StoreDataTimeKey = 'today' | 'yesterday' | 'week' | 'seven' | 'month' | 'thirty';
type StoreDataSortKey = 'default_ranking' | 'high_to_low' | 'low_to_high';
type FavoriteStatusFilter = 'all' | 'online' | 'offline';

interface ActiveFilter {
	device: DeviceFilter;
	status: StatusFilter;
	fav: FavFilter;
	sort: SortKey;
}

const DEFAULT_FILTER: ActiveFilter = {
	device: 'any',
	status: 'any',
	fav: 'any',
	sort: 'create',
};

const APAC_DEFAULT_SORT: ApacSortKey = 'default';
const STORE_DATA_DEFAULT_METRIC: ApacStoreDataMetricKey = 'entry_number';
const STORE_DATA_DEFAULT_TIME: StoreDataTimeKey = 'today';
const STORE_DATA_DEFAULT_SORT: StoreDataSortKey = 'high_to_low';
const FAVORITE_DATA_DEFAULT_TIME: StoreDataTimeKey = 'seven';
const FAVORITE_DATA_DEFAULT_SORT: StoreDataSortKey = 'default_ranking';


const PRIMARY = '#FF7043';

export default function VideoTab() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const region = useRegion();
	const [stores, setStores] = useState<Store[] | null>(null);
	const [groups, setGroups] = useState<StoreGroup[] | null>(null);
	const [devicesByStore, setDevicesByStore] = useState<Record<string, DeviceLite[]>>({});
	const [tab, setTab] = useState<'all' | 'fav'>('all');
	const [search, setSearch] = useState('');
	const [filter, setFilter] = useState<ActiveFilter>(DEFAULT_FILTER);
	const [filterOpen, setFilterOpen] = useState(false);
	const [organizationOpen, setOrganizationOpen] = useState(false);
	const [sortOpen, setSortOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const [activeGroupId, setActiveGroupId] = useState('all');
	const [apacSort, setApacSort] = useState<ApacSortKey>(APAC_DEFAULT_SORT);
	const [apacStoreTab, setApacStoreTab] = useState<ApacStoreTab>('video');
	const [storeDataStoreId, setStoreDataStoreId] = useState('');
	const [multiStoreOpen, setMultiStoreOpen] = useState(false);
	const [storeError, setStoreError] = useState<string | null>(null);
	const [groupError, setGroupError] = useState<string | null>(null);
	const [deviceError, setDeviceError] = useState<string | null>(null);
	const [apacFavoriteStoreIds, setApacFavoriteStoreIds] = useState<string[]>([]);
	const [storeDataMetric, setStoreDataMetric] = useState<ApacStoreDataMetricKey>(STORE_DATA_DEFAULT_METRIC);
	const [storeDataTime, setStoreDataTime] = useState<StoreDataTimeKey>(STORE_DATA_DEFAULT_TIME);
	const [storeDataSort, setStoreDataSort] = useState<StoreDataSortKey>(STORE_DATA_DEFAULT_SORT);
	const [storeDataSortOpen, setStoreDataSortOpen] = useState(false);
	const [storeDataFilterOpen, setStoreDataFilterOpen] = useState(false);
	const [favoriteStatus, setFavoriteStatus] = useState<FavoriteStatusFilter>('all');
	const [favoriteDataMetric, setFavoriteDataMetric] = useState<ApacStoreDataMetricKey>(STORE_DATA_DEFAULT_METRIC);
	const [favoriteDataTime, setFavoriteDataTime] = useState<StoreDataTimeKey>(FAVORITE_DATA_DEFAULT_TIME);
	const [favoriteDataSort, setFavoriteDataSort] = useState<StoreDataSortKey>(FAVORITE_DATA_DEFAULT_SORT);
	const [favoriteDataMetricOpen, setFavoriteDataMetricOpen] = useState(false);
	const [favoriteDataSortOpen, setFavoriteDataSortOpen] = useState(false);
	const [favoriteDataTimeOpen, setFavoriteDataTimeOpen] = useState(false);
	const [favoriteDataMetricLoading, setFavoriteDataMetricLoading] = useState(false);
	const [storeDataMetricCache, setStoreDataMetricCache] = useState<Record<string, Record<string, number | null>>>({});
	const [storeDataMetricLoading, setStoreDataMetricLoading] = useState(false);
	const [favoriteStatusStoreIds, setFavoriteStatusStoreIds] = useState<string[] | null>(null);
	const [favoriteStatusLoading, setFavoriteStatusLoading] = useState(false);
	const [favoritePendingStoreIds, setFavoritePendingStoreIds] = useState<string[]>([]);
	const [phoneStore, setPhoneStore] = useState<Store | null>(null);
	const favoriteStoreIds = useGlobalStore((s) => s.favoriteStoreIds);
	const toggleFavorite = useGlobalStore((s) => s.toggleFavorite);
	const pageMode: StorePageMode = searchParams.get('favorite') === '1' ? 'favorite' : 'normal';

	useEffect(() => {
		setStoreError(null);
		callInternal<Store[]>({ short_name: 'base', action: '/store/visible_stores' })
			.then((res) => {
				if (res.code === 0 && Array.isArray(res.data)) {
					setStores(res.data);
				} else {
					setStoreError(`${res.msg || 'visible_stores failed'} (code=${res.code})`);
					setStores([]);
				}
			})
			.catch((e) => {
				setStoreError(e instanceof Error ? e.message : String(e));
				setStores([]);
			});
	}, []);

	useEffect(() => {
		if (!stores || stores.length === 0) {
			setDevicesByStore({});
			setDeviceError(null);
			return;
		}
		setDeviceError(null);
		// iOS 用 /fastDevice/channel_list（仅返回可播放通道，与 iOS UI 一致）
		// 与 /store_device_list_with_status（返回所有绑定设备含未绑通道）不同
		Promise.allSettled(stores.map(async (s) => {
			const chs = await fetchFastChannels(s.store_id);
			return [s.store_id, chs.map(fastChannelToDevice)] as const;
		})).then((results) => {
			const grouped: Record<string, DeviceLite[]> = {};
			const failures: string[] = [];
			results.forEach((result, index) => {
				const store = stores[index];
				if (result.status === 'fulfilled') {
					const [sid, devs] = result.value;
					grouped[sid] = devs;
				} else if (store) {
					failures.push(`${store.store}: ${result.reason instanceof Error ? result.reason.message : String(result.reason)}`);
				}
			});
			setDevicesByStore(grouped);
			setDeviceError(failures.length ? `Device list partially failed: ${failures.slice(0, 3).join('; ')}` : null);
		});
	}, [stores]);

	useEffect(() => {
		if (region !== 'apac') {
			setGroups(null);
			setGroupError(null);
			setActiveGroupId('all');
			return;
		}
		setGroupError(null);
		callInternal<{ items?: StoreGroup[] } | StoreGroup[]>({
			short_name: 'standard',
			action: '/business/group/store/all',
		})
			.then((res) => {
				if (res.code === 0) {
					const data = res.data;
					setGroups(Array.isArray(data) ? data : data?.items ?? []);
				} else {
					setGroupError(`${res.msg || 'group/store/all failed'} (code=${res.code})`);
					setGroups([]);
				}
			})
			.catch((e) => {
				setGroupError(e instanceof Error ? e.message : String(e));
				setGroups([]);
			});
	}, [region]);

	useEffect(() => {
		if (region !== 'apac') {
			setApacFavoriteStoreIds([]);
			return;
		}
		let alive = true;
		void fetchApacCollectedStoreIds().then((ids) => {
			if (alive) setApacFavoriteStoreIds(ids);
		});
		return () => {
			alive = false;
		};
	}, [region]);

	useEffect(() => {
		if (region !== 'apac' || pageMode !== 'favorite' || favoriteStatus === 'all') {
			setFavoriteStatusStoreIds(null);
			setFavoriteStatusLoading(false);
			return;
		}
		let alive = true;
		setFavoriteStatusLoading(true);
		void fetchApacStoreIdsByChannelStatus(favoriteStatus)
			.then((ids) => {
				if (alive) setFavoriteStatusStoreIds(ids.map(String));
			})
			.catch(() => {
				if (alive) setFavoriteStatusStoreIds(null);
			})
			.finally(() => {
				if (alive) setFavoriteStatusLoading(false);
			});
		return () => {
			alive = false;
		};
	}, [favoriteStatus, pageMode, region]);

	const updateApacFavoriteIds = (storeId: string, collected: boolean) => {
		const normalizedStoreId = String(storeId);
		setApacFavoriteStoreIds((current) => {
			const ids = current.map(String);
			if (collected) return ids.includes(normalizedStoreId) ? ids : [...ids, normalizedStoreId];
			return ids.filter((id) => id !== normalizedStoreId);
		});
		setFavoriteStatusStoreIds((current) => {
			if (!current) return current;
			const ids = current.map(String);
			if (collected) return ids.includes(normalizedStoreId) ? ids : [...ids, normalizedStoreId];
			return ids.filter((id) => id !== normalizedStoreId);
		});
	};

	const toggleApacStoreFavorite = async (storeId: string) => {
		const normalizedStoreId = String(storeId);
		if (!normalizedStoreId || favoritePendingStoreIds.includes(normalizedStoreId)) return;
		const nextCollected = !apacFavoriteStoreIds.map(String).includes(normalizedStoreId);
		setFavoritePendingStoreIds((current) => current.includes(normalizedStoreId) ? current : [...current, normalizedStoreId]);
		try {
			await setApacStoreCollected(normalizedStoreId, nextCollected);
			updateApacFavoriteIds(normalizedStoreId, nextCollected);
		} catch {
			antdMessage.error(t('native.common.operation_failed'));
		} finally {
			setFavoritePendingStoreIds((current) => current.filter((id) => id !== normalizedStoreId));
		}
	};

	const storeDataRange = useMemo(() => getStoreDataDateRange(storeDataTime), [storeDataTime]);
	const storeDataCacheKey = useMemo(
		() => makeStoreDataCacheKey(storeDataMetric, storeDataRange.startDate, storeDataRange.endDate),
		[storeDataMetric, storeDataRange.endDate, storeDataRange.startDate],
	);
	const favoriteDataRange = useMemo(() => getStoreDataDateRange(favoriteDataTime), [favoriteDataTime]);
	const favoriteDataCacheKey = useMemo(
		() => makeStoreDataCacheKey(favoriteDataMetric, favoriteDataRange.startDate, favoriteDataRange.endDate),
		[favoriteDataMetric, favoriteDataRange.endDate, favoriteDataRange.startDate],
	);

	useEffect(() => {
		if (region !== 'apac' || !stores || stores.length === 0) {
			setStoreDataMetricLoading(false);
			return;
		}
		const cached = storeDataMetricCache[storeDataCacheKey] ?? {};
		const missingStoreIds = stores
			.map((store) => String(store.store_id))
			.filter((storeId) => !Object.prototype.hasOwnProperty.call(cached, storeId));
		if (missingStoreIds.length === 0) {
			setStoreDataMetricLoading(false);
			return;
		}
		let alive = true;
		setStoreDataMetricLoading(true);
		void fetchApacStoreDataMetricValues({
			storeIds: missingStoreIds,
			metric: storeDataMetric,
			startDate: storeDataRange.startDate,
			endDate: storeDataRange.endDate,
		})
			.then((values) => {
				if (!alive) return;
				setStoreDataMetricCache((prev) => ({
					...prev,
					[storeDataCacheKey]: {
						...(prev[storeDataCacheKey] ?? {}),
						...values,
					},
				}));
			})
			.finally(() => {
				if (alive) setStoreDataMetricLoading(false);
			});
		return () => {
			alive = false;
		};
	}, [region, storeDataCacheKey, storeDataMetric, storeDataMetricCache, storeDataRange.endDate, storeDataRange.startDate, stores]);

	useEffect(() => {
		if (region !== 'apac' || pageMode !== 'favorite' || !stores || stores.length === 0 || apacFavoriteStoreIds.length === 0) {
			setFavoriteDataMetricLoading(false);
			return;
		}
		const cached = storeDataMetricCache[favoriteDataCacheKey] ?? {};
		const favSet = new Set(apacFavoriteStoreIds.map(String));
		const missingStoreIds = stores
			.map((store) => String(store.store_id))
			.filter((storeId) => favSet.has(storeId))
			.filter((storeId) => !Object.prototype.hasOwnProperty.call(cached, storeId));
		if (missingStoreIds.length === 0) {
			setFavoriteDataMetricLoading(false);
			return;
		}
		let alive = true;
		setFavoriteDataMetricLoading(true);
		void fetchApacStoreDataMetricValues({
			storeIds: missingStoreIds,
			metric: favoriteDataMetric,
			startDate: favoriteDataRange.startDate,
			endDate: favoriteDataRange.endDate,
		})
			.then((values) => {
				if (!alive) return;
				setStoreDataMetricCache((prev) => ({
					...prev,
					[favoriteDataCacheKey]: {
						...(prev[favoriteDataCacheKey] ?? {}),
						...values,
					},
				}));
			})
			.finally(() => {
				if (alive) setFavoriteDataMetricLoading(false);
			});
		return () => {
			alive = false;
		};
	}, [
		apacFavoriteStoreIds,
		favoriteDataCacheKey,
		favoriteDataMetric,
		favoriteDataRange.endDate,
		favoriteDataRange.startDate,
		pageMode,
		region,
		storeDataMetricCache,
		stores,
	]);

	const allDevices = Object.values(devicesByStore).reduce<DeviceLite[]>((all, devices) => all.concat(devices), []);
	const onlineRate = (() => {
		if (allDevices.length === 0) return null;
		const online = allDevices.filter((d) => String(d.status) === '1').length;
		return Math.round((online / allDevices.length) * 10000) / 100;
	})();
	const onlineDevices = allDevices.filter((d) => String(d.status) === '1').length;
	const offlineDevices = allDevices.length - onlineDevices;
	const monitorCaps = useMemo(
		() => new Set(capabilitiesForArea('monitor', region, ['available']).map((cap) => cap.key)),
		[region],
	);

	const filtered = useMemo(() => {
		const all = stores ?? [];
		const favSet = new Set(favoriteStoreIds);

		const passes = all.filter((s) => {
			// 搜索
			if (search && !s.store.includes(search) && !s.store_code.includes(search)) return false;

			// 顶部 tab：收藏
			if (tab === 'fav' && !favSet.has(s.store_id)) return false;

			// 筛选 Drawer：门店设备
			if (filter.device === 'has'  && Number(s.has_device) !== 1) return false;
			if (filter.device === 'none' && Number(s.has_device) === 1) return false;

			// 设备状态：在线=至少一台在线；离线=存在离线通道，便于运营直接定位异常门店
			if (filter.status !== 'any') {
				const ds = devicesByStore[s.store_id] ?? [];
				const anyOnline = ds.some((d) => String(d.status) === '1');
				const anyOffline = ds.some((d) => String(d.status) !== '1');
				if (filter.status === 'online'  && !anyOnline) return false;
				if (filter.status === 'offline' && !anyOffline) return false;
			}

			// 收藏状态（Drawer，独立于顶部 tab）
			if (filter.fav === 'fav'   && !favSet.has(s.store_id)) return false;
			if (filter.fav === 'unfav' &&  favSet.has(s.store_id)) return false;

			return true;
		});

		// 排序
		const sorted = [...passes];
		if (filter.sort === 'name') {
			sorted.sort((a, b) => a.store.localeCompare(b.store, normalizeLocale(i18n.language)));
		} else {
			// 按创建时间倒序（新 → 旧）
			sorted.sort((a, b) => {
				const at = Number(a.create_time ?? 0);
				const bt = Number(b.create_time ?? 0);
				return bt - at;
			});
		}
		return sorted;
	}, [stores, search, tab, filter, favoriteStoreIds, devicesByStore, i18n.language]);

	const filterActiveCount = useMemo(() => {
		let n = 0;
		if (filter.device !== 'any') n++;
		if (filter.status !== 'any') n++;
		if (filter.fav    !== 'any') n++;
		if (filter.sort   !== 'create') n++;
		return n;
	}, [filter]);
	const multiViewStores = useMemo(() => {
		return (stores ?? [])
			.map((store) => {
				const devices = devicesByStore[store.store_id] ?? [];
				return {
					store,
					total: devices.length,
					online: devices.filter((d) => String(d.status) === '1' && d.sn).length,
				};
			})
			.sort((a, b) => {
				if (b.online !== a.online) return b.online - a.online;
				return a.store.store.localeCompare(b.store.store, normalizeLocale(i18n.language));
			});
	}, [stores, devicesByStore, i18n.language]);
	const storeSummaryKey = stores?.length === 1 ? 'mtab.video.summary_one' : 'mtab.video.summary';
	const openOfflineStores = () => {
		setTab('all');
		setSearch('');
		setFilter((prev) => ({ ...prev, device: 'has', status: 'offline' }));
	};
	const storeDetailUrl = (s: Store, focusOffline = false) => {
		const params = new URLSearchParams({ storeName: s.store });
		if (focusOffline) params.set('status', 'offline');
		return `/m/video/${s.store_id}?${params.toString()}`;
	};

	const organizationOptions = useMemo(() => normalizeStoreGroups(groups), [groups]);
	const activeOrganizationLabel = useMemo(() => {
		if (activeGroupId === 'all') return t('store_video.organization');
		return organizationOptions.find((g) => g.id === activeGroupId)?.name ?? t('store_video.organization');
	}, [activeGroupId, organizationOptions, t]);
	const apacSortLabel =
		apacSort === 'create_desc' ? t('store_video.sort_create')
		: apacSort === 'name_az' ? t('store_video.sort_name')
		: t('store_video.sort_default');
	const apacFilterActiveCount = useMemo(() => {
		let n = 0;
		if (filter.device !== 'any') n++;
		if (filter.status !== 'any') n++;
		if (filter.fav !== 'any') n++;
		return n;
	}, [filter]);
	const apacAllRows = useMemo<StoreVideoRow[]>(() => {
		return (stores ?? []).map((store, index) => {
			const devices = devicesByStore[store.store_id] ?? [];
			return {
				store,
				index,
				stats: getStoreStats(devices),
				devices,
			};
		});
	}, [devicesByStore, stores]);
	const apacBaseRows = useMemo<StoreVideoRow[]>(() => {
		const query = search.trim().toLocaleLowerCase(normalizeLocale(i18n.language));
		return apacAllRows.filter(({ store }) => {
			const code = store.store_code ?? '';
			if (query && !store.store.toLocaleLowerCase(normalizeLocale(i18n.language)).includes(query) && !code.toLocaleLowerCase(normalizeLocale(i18n.language)).includes(query)) return false;
			if (activeGroupId !== 'all' && !storeMatchesGroup(store, activeGroupId)) return false;
			return true;
		});
	}, [activeGroupId, apacAllRows, i18n.language, search]);
	const apacRows = useMemo<StoreVideoRow[]>(() => {
		const favSet = new Set(apacFavoriteStoreIds);
		const visible = apacBaseRows.filter(({ store, stats }) => {
			const hasDevice = Number(store.has_device) === 1 || stats.total > 0;
			if (filter.device === 'has' && !hasDevice) return false;
			if (filter.device === 'none' && hasDevice) return false;
			if (filter.status === 'online' && stats.online === 0) return false;
			if (filter.status === 'offline' && stats.offline === 0) return false;
			if (filter.fav === 'fav' && !favSet.has(store.store_id)) return false;
			if (filter.fav === 'unfav' && favSet.has(store.store_id)) return false;
			return true;
		});
		const sorted = [...visible];
		if (apacSort === 'create_desc') {
			sorted.sort((a, b) => toCreateTime(b.store.create_time) - toCreateTime(a.store.create_time));
		} else if (apacSort === 'name_az') {
			sorted.sort((a, b) => a.store.store.localeCompare(b.store.store, normalizeLocale(i18n.language)));
		} else {
			sorted.sort((a, b) => a.index - b.index);
		}
		return sorted;
	}, [apacBaseRows, apacFavoriteStoreIds, apacSort, filter, i18n.language]);
	const storeDataMetricValues = storeDataMetricCache[storeDataCacheKey] ?? {};
	const storeDataRows = useMemo<StoreVideoRow[]>(() => {
		const sorted = [...apacBaseRows];
		sorted.sort((a, b) => compareStoreDataRows(a, b, storeDataMetricValues, storeDataSort, normalizeLocale(i18n.language)));
		return sorted;
	}, [apacBaseRows, i18n.language, storeDataMetricValues, storeDataSort]);
	const favoriteStoreIdSet = useMemo(() => new Set(apacFavoriteStoreIds.map(String)), [apacFavoriteStoreIds]);
	const favoriteVideoRows = useMemo(() => {
		const statusIdSet = favoriteStatusStoreIds ? new Set(favoriteStatusStoreIds.map(String)) : null;
		return apacAllRows.filter((row) => {
			const { store, stats } = row;
			const storeId = String(store.store_id);
			if (!favoriteStoreIdSet.has(storeId)) return false;
			if (favoriteStatus === 'all') return true;
			const hasMatchingDevice = getFavoriteVisibleDevices(row, favoriteStatus).length > 0;
			if (statusIdSet) return statusIdSet.has(storeId) || hasMatchingDevice;
			if (favoriteStatus === 'online') return stats.online > 0;
			if (favoriteStatus === 'offline') return stats.offline > 0;
			return true;
		});
	}, [apacAllRows, favoriteStatus, favoriteStatusStoreIds, favoriteStoreIdSet]);
	const favoriteDataMetricValues = storeDataMetricCache[favoriteDataCacheKey] ?? {};
	const favoriteDataRows = useMemo(() => {
		const rows = apacAllRows.filter(({ store }) => favoriteStoreIdSet.has(String(store.store_id)));
		const sorted = [...rows];
		sorted.sort((a, b) => compareStoreDataRows(a, b, favoriteDataMetricValues, favoriteDataSort, normalizeLocale(i18n.language)));
		return sorted;
	}, [apacAllRows, favoriteDataMetricValues, favoriteDataSort, favoriteStoreIdSet, i18n.language]);
	const apacOnlineRate = useMemo(() => {
		const total = apacRows.reduce((sum, row) => sum + row.stats.total, 0);
		if (total === 0) return null;
		const online = apacRows.reduce((sum, row) => sum + row.stats.online, 0);
		return Math.round((online / total) * 10000) / 100;
	}, [apacRows]);
	useEffect(() => {
		if (region !== 'apac' || !stores) return;
		if (stores.length === 0) {
			if (storeDataStoreId) setStoreDataStoreId('');
			return;
		}
		if (!storeDataStoreId || !stores.some((store) => String(store.store_id) === String(storeDataStoreId))) {
			setStoreDataStoreId(stores[0].store_id);
		}
	}, [region, storeDataStoreId, stores]);

	if (region === 'apac') {
		const storeDataMetricLabel = getStoreDataMetricLabel(storeDataMetric, t);
		const storeDataTimeLabel = getStoreDataTimeLabel(storeDataTime, t);
		const storeDataSortLabel = getStoreDataSortLabel(storeDataSort, t);
		const favoriteDataMetricLabel = getStoreDataMetricLabel(favoriteDataMetric, t);
		const favoriteDataTimeLabel = getStoreDataTimeLabel(favoriteDataTime, t);
		const favoriteDataSortLabel = getStoreDataSortLabel(favoriteDataSort, t);
		const openFavoritePage = () => {
			setSearch('');
			setSearchOpen(false);
			navigate('/m/video?favorite=1');
		};
		const closeFavoritePage = () => {
			setSearch('');
			setSearchOpen(false);
			navigate('/m/video');
		};
		const sheets = (
			<>
				<OrganizationDrawer
					open={organizationOpen}
					loading={groups === null}
					value={activeGroupId}
					groups={organizationOptions}
					onClose={() => setOrganizationOpen(false)}
					onPick={(id) => {
						setActiveGroupId(id);
						setOrganizationOpen(false);
					}}
				/>
				<FilterDrawer
					open={filterOpen}
					onClose={() => setFilterOpen(false)}
					value={filter}
					showSort={false}
					onApply={(next) => { setFilter(next); setFilterOpen(false); }}
				/>
				<SortDrawer
					open={sortOpen}
					value={apacSort}
					onClose={() => setSortOpen(false)}
					onPick={(next) => {
						setApacSort(next);
						setSortOpen(false);
					}}
				/>
				<StoreDataSortSheet
					open={storeDataSortOpen}
					value={storeDataSort}
					includeDefault={false}
					onClose={() => setStoreDataSortOpen(false)}
					onPick={(next) => {
						setStoreDataSort(next);
						setStoreDataSortOpen(false);
					}}
				/>
				<StoreDataFilterSheet
					open={storeDataFilterOpen}
					metric={storeDataMetric}
					time={storeDataTime}
					onClose={() => setStoreDataFilterOpen(false)}
					onApply={(nextMetric, nextTime) => {
						setStoreDataMetric(nextMetric);
						setStoreDataTime(nextTime);
						setStoreDataFilterOpen(false);
					}}
				/>
				<StoreDataMetricSheet
					open={favoriteDataMetricOpen}
					value={favoriteDataMetric}
					onClose={() => setFavoriteDataMetricOpen(false)}
					onPick={(next) => {
						setFavoriteDataMetric(next);
						setFavoriteDataMetricOpen(false);
					}}
				/>
				<StoreDataSortSheet
					open={favoriteDataSortOpen}
					value={favoriteDataSort}
					includeDefault
					onClose={() => setFavoriteDataSortOpen(false)}
					onPick={(next) => {
						setFavoriteDataSort(next);
						setFavoriteDataSortOpen(false);
					}}
				/>
				<StoreDataTimeSheet
					open={favoriteDataTimeOpen}
					value={favoriteDataTime}
					includeToday={false}
					onClose={() => setFavoriteDataTimeOpen(false)}
					onPick={(next) => {
						setFavoriteDataTime(next);
						setFavoriteDataTimeOpen(false);
					}}
				/>
				<ApacPhoneSheet store={phoneStore} onClose={() => setPhoneStore(null)} />
			</>
		);
		if (pageMode === 'favorite') {
			return (
				<>
					<ApacFavoriteStoreView
						activeTab={apacStoreTab}
						videoRows={favoriteVideoRows}
						dataRows={favoriteDataRows}
						storesLoading={!stores || favoriteStatusLoading}
						selectedDataStoreId={storeDataStoreId}
						favoriteStatus={favoriteStatus}
						storeDataMetric={favoriteDataMetric}
						storeDataMetricLabel={favoriteDataMetricLabel}
						storeDataSortLabel={favoriteDataSortLabel}
						storeDataTimeLabel={favoriteDataTimeLabel}
						storeDataValues={favoriteDataMetricValues}
						storeDataLoading={favoriteDataMetricLoading}
						favoriteStoreIds={apacFavoriteStoreIds}
						favoritePendingStoreIds={favoritePendingStoreIds}
						onBack={closeFavoritePage}
						onTabChange={setApacStoreTab}
						onFavoriteStatusChange={setFavoriteStatus}
						onDataStoreChange={setStoreDataStoreId}
						onOpenFavoriteDataMetric={() => setFavoriteDataMetricOpen(true)}
						onOpenFavoriteDataSort={() => setFavoriteDataSortOpen(true)}
						onOpenFavoriteDataTime={() => setFavoriteDataTimeOpen(true)}
						onStoreFavorite={toggleApacStoreFavorite}
						onOpenPhone={setPhoneStore}
						onOpenStore={(store, focusOffline) => navigate(storeDetailUrl(store, focusOffline))}
						onOpenMultiView={(store) => navigate(`/m/multi-view/${store.store_id}?storeName=${encodeURIComponent(store.store)}`)}
					/>
					{sheets}
				</>
			);
		}
		return (
			<>
				<ApacStoreVideoView
					activeTab={apacStoreTab}
					videoRows={apacRows}
					dataRows={storeDataRows}
					storesLoading={!stores}
					onlineRate={apacOnlineRate}
					selectedDataStoreId={storeDataStoreId}
					search={search}
					searchOpen={searchOpen}
					filterActiveCount={apacFilterActiveCount}
					organizationLabel={activeOrganizationLabel}
					sortLabel={apacSortLabel}
					storeDataMetric={storeDataMetric}
					storeDataMetricLabel={storeDataMetricLabel}
					storeDataSortLabel={storeDataSortLabel}
					storeDataTimeLabel={storeDataTimeLabel}
					storeDataValues={storeDataMetricValues}
					storeDataLoading={storeDataMetricLoading}
					onTabChange={setApacStoreTab}
					onDataStoreChange={setStoreDataStoreId}
					onSearchChange={setSearch}
					onToggleSearch={() => {
						setSearchOpen((open) => {
							if (open) setSearch('');
							return !open;
						});
					}}
					onOpenFavorites={openFavoritePage}
					onStoreFavorite={toggleApacStoreFavorite}
					favoriteStoreIds={apacFavoriteStoreIds}
					favoritePendingStoreIds={favoritePendingStoreIds}
					onOpenOrganization={() => setOrganizationOpen(true)}
					onOpenFilter={() => setFilterOpen(true)}
					onOpenSort={() => setSortOpen(true)}
					onOpenStoreDataOrganization={() => setOrganizationOpen(true)}
					onOpenStoreDataSort={() => setStoreDataSortOpen(true)}
					onOpenStoreDataFilter={() => setStoreDataFilterOpen(true)}
					onOpenPhone={setPhoneStore}
					onOpenStore={(store, focusOffline) => navigate(storeDetailUrl(store, focusOffline))}
					onOpenMultiView={(store) => navigate(`/m/multi-view/${store.store_id}?storeName=${encodeURIComponent(store.store)}`)}
				/>
				{sheets}
			</>
		);
	}

	return (
		<MobileRootPage>
			<RootHeader
				icon={<VideoCameraOutlined />}
				title={t('tabbar.monitor')}
				subtitle={t('root.monitor.subtitle')}
				meta={<RegionTag region={region} />}
			/>

			<SummaryStrip
				items={[
					{ key: 'stores', label: t('root.monitor.store_count'), value: stores ? stores.length : '—' },
					{ key: 'online', label: t('root.monitor.online_devices'), value: allDevices.length ? onlineDevices : '—', tone: 'success' },
					{
						key: 'offline',
						label: t('root.monitor.offline_devices'),
						value: allDevices.length ? offlineDevices : '—',
						tone: offlineDevices > 0 ? 'danger' : 'default',
						hint: offlineDevices > 0
							? t(filter.status === 'offline' ? 'root.monitor.offline_filter_active' : 'root.monitor.offline_action')
							: undefined,
						active: filter.status === 'offline',
						onClick: offlineDevices > 0 ? openOfflineStores : undefined,
					},
					{ key: 'fav', label: t('root.monitor.favorites'), value: favoriteStoreIds.length },
				]}
			/>

			<SectionBlock title={t('root.monitor.tools')}>
				<ActionTileGrid
					items={[
						...(monitorCaps.has('store') ? [{
							key: 'store-management',
							title: t('root.monitor.tool_store'),
							subtitle: t('root.monitor.tool_store_hint'),
							icon: <ShopOutlined />,
							onClick: () => navigate('/store'),
						}] : []),
						...(monitorCaps.has('device') ? [{
							key: 'device-status',
							title: t('root.monitor.tool_device'),
							subtitle: t('root.monitor.tool_device_hint'),
							icon: <AppstoreOutlined />,
							onClick: () => navigate('/device'),
						}] : []),
						{
							key: 'multi-view',
							title: t('root.monitor.tool_multi_view'),
							subtitle: stores && stores.length > 0
								? t('root.monitor.tool_multi_view_hint')
								: t('root.monitor.tool_multi_view_empty'),
							icon: <VideoCameraOutlined />,
							disabled: !!stores && stores.length === 0,
							onClick: () => setMultiStoreOpen(true),
						},
					]}
				/>
			</SectionBlock>

			<SectionBlock
				title={t('root.monitor.stores')}
				extra={
					<button
						type="button"
						onClick={() => setFilterOpen(true)}
						style={{
							border: 0,
							background: 'transparent',
							color: filterActiveCount > 0 ? MOBILE_ROOT_PRIMARY : '#697386',
							fontSize: 13,
							cursor: 'pointer',
							minHeight: 32,
						}}
					>
						<FilterOutlined /> {t('mtab.video.filter')}
						{filterActiveCount > 0 && (
							<span style={{
								marginInlineStart: 4,
								display: 'inline-block',
								minWidth: 16,
								height: 16,
								lineHeight: '16px',
								textAlign: 'center',
								borderRadius: 8,
								background: MOBILE_ROOT_PRIMARY,
								color: '#fff',
								fontSize: 10,
								padding: '0 4px',
							}}>{filterActiveCount}</span>
						)}
					</button>
				}
			>
				<Typography.Text type="secondary" style={{ display: 'block', margin: '-4px 4px 10px', fontSize: 12 }}>
					{t('mtab.video.brand_full')}
				</Typography.Text>

				<Input
					prefix={<SearchOutlined style={{ color: '#999' }} />}
					placeholder={t('mtab.video.search_placeholder')}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					style={{ borderRadius: 8, background: '#fff' }}
					size="large"
				/>

				<div style={{ marginTop: 12 }}>
					<Tabs
						activeKey={tab}
						onChange={(k) => setTab(k as 'all' | 'fav')}
						tabBarStyle={{ borderBottom: 'none', margin: 0 }}
						items={[
							{ key: 'all', label: t('mtab.video.tab_all') },
							{ key: 'fav', label: t('mtab.video.tab_fav') },
						]}
					/>
				</div>

				{stores && (
					<Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
						{t(storeSummaryKey, {
							count: stores.length,
							rate: onlineRate != null ? `${onlineRate}%` : '—',
						})}
					</Typography.Text>
				)}
				{storeError && <Alert type="warning" showIcon message={storeError} style={{ marginTop: 12 }} />}
				{deviceError && <Alert type="warning" showIcon message={deviceError} style={{ marginTop: 12 }} />}
			</SectionBlock>

			<div style={{
				// 桌面 ≥ 760 ≈ 2 列，≥ 1140 ≈ 3 列；手机视口 < 380 用 min(380,100%) 钳到 1fr 防溢出
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))',
				gap: 12,
			}}>
				{!stores && <Spin />}
				{stores && filtered.length === 0 && <Empty description={t('common.no_data')} />}
				{filtered.map((s) => {
					const allDevs = devicesByStore[s.store_id] ?? [];
					// 仅展示有 name 的（无 name 的 NVR 折叠在详情页里），按 name+在线优先排序
					const namedDevs = allDevs
						.filter((d) => d.name && d.name.trim())
						.sort((a, b) => {
							const aOn = String(a.status) === '1' ? 0 : 1;
							const bOn = String(b.status) === '1' ? 0 : 1;
							return aOn - bOn;
						});
					const offline = allDevs.filter((d) => String(d.status) !== '1').length;
					const previewCount = 6;
					const devs = namedDevs;
					const showMore = devs.length > previewCount;
					return (
						<Card
							key={s.store_id}
							style={{ borderRadius: 8, borderColor: '#e5e7eb' }}
							styles={{ body: { padding: 14 } }}
						>
							<div
								onClick={() => navigate(storeDetailUrl(s, offline > 0))}
								style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
							>
								<div>
									<Typography.Text strong style={{ fontSize: 16 }}>
										{s.store}
									</Typography.Text>
									<RightOutlined style={{ color: '#bfbfbf', marginInlineStart: 4, fontSize: 12 }} />
									<div style={{ marginTop: 4, fontSize: 12 }}>
										{offline > 0 ? (
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													navigate(storeDetailUrl(s, true));
												}}
												style={{
													border: 0,
													background: 'transparent',
													padding: 0,
													color: '#697386',
													cursor: 'pointer',
													fontSize: 12,
													textAlign: 'start',
												}}
											>
												<span style={{ color: PRIMARY, fontSize: 18, marginInlineEnd: 4 }}>•</span>
												{t(offline === 1 ? 'mtab.video.offline_count_one' : 'mtab.video.offline_count', { n: offline })}
											</button>
										) : devs.length > 0 ? (
											<>
												<span style={{ color: '#52c41a', fontSize: 18, marginInlineEnd: 4 }}>•</span>
												<Typography.Text type="secondary">{t('mtab.video.all_online')}</Typography.Text>
											</>
										) : (
											<Typography.Text type="secondary" style={{ fontSize: 12 }}>
												{t('mtab.video.no_device')}
											</Typography.Text>
										)}
									</div>
								</div>
								<AppstoreOutlined
									style={{ fontSize: 22, color: PRIMARY, cursor: 'pointer' }}
									onClick={(e) => {
										e.stopPropagation();
										navigate(`/m/multi-select/${s.store_id}?storeName=${encodeURIComponent(s.store)}`);
									}}
								/>
							</div>

							{devs.length > 0 && (
								<>
									<div style={{ height: 1, background: '#f0f0f0', margin: '12px 0' }} />
									<Space size={[8, 8]} wrap>
										{devs.slice(0, previewCount).map((d) => {
											const isOnline = String(d.status) === '1';
											const statusLabel = t(isOnline ? 'mtab.video.device_online' : 'mtab.video.device_offline');
											return (
												<Tag
													key={d.device_id}
													aria-label={`${deviceLabel(d)} ${statusLabel}`}
													icon={
														<span
															style={{
																display: 'inline-block',
																width: 14,
																height: 14,
																borderRadius: 3,
																background: isOnline ? PRIMARY : '#bfbfbf',
																marginInlineEnd: 4,
																verticalAlign: 'middle',
															}}
														/>
													}
													style={{
														padding: '4px 9px',
														borderRadius: 6,
														background: isOnline ? '#fafafa' : '#f8f9fb',
														border: isOnline ? 'none' : '1px dashed #c9ced6',
														color: isOnline ? '#333' : '#999',
														fontSize: 13,
														cursor: isOnline ? 'pointer' : 'not-allowed',
													}}
													onClick={() => {
														if (!isOnline) {
															navigate(storeDetailUrl(s, true));
															return;
														}
														navigate(
															`/m/player?storeId=${s.store_id}&storeName=${encodeURIComponent(s.store)}` +
															`&deviceSn=${d.sn ?? ''}&deviceLabel=${encodeURIComponent(deviceLabel(d))}` +
															`&channel=${d.channel_id ?? '1'}`,
														);
													}}
												>
													<span>{deviceLabel(d)}</span>
													<span style={{ marginInlineStart: 6, color: isOnline ? '#15803d' : '#9aa3af', fontSize: 11 }}>
														{statusLabel}
													</span>
												</Tag>
											);
										})}
									</Space>
									{showMore && (
										<div
											style={{ textAlign: 'center', marginTop: 12, color: '#999', fontSize: 13, cursor: 'pointer' }}
											onClick={() => navigate(storeDetailUrl(s, offline > 0))}
										>
											{t('mtab.video.view_all')} <RightOutlined style={{ fontSize: 11 }} />
										</div>
									)}
								</>
							)}
						</Card>
					);
				})}
			</div>

			<FilterDrawer
				open={filterOpen}
				onClose={() => setFilterOpen(false)}
				value={filter}
				onApply={(next) => { setFilter(next); setFilterOpen(false); }}
			/>
			<MultiViewStoreDrawer
				open={multiStoreOpen}
				stores={multiViewStores}
				loading={!stores}
				onClose={() => setMultiStoreOpen(false)}
				onPick={(s) => {
					setMultiStoreOpen(false);
					navigate(`/m/multi-select/${s.store_id}?storeName=${encodeURIComponent(s.store)}`);
				}}
			/>
		</MobileRootPage>
	);
}

function ApacStoreVideoView({
	activeTab,
	videoRows,
	dataRows,
	storesLoading,
	onlineRate,
	selectedDataStoreId,
	search,
	searchOpen,
	filterActiveCount,
	organizationLabel,
	sortLabel,
	storeDataMetric,
	storeDataMetricLabel,
	storeDataSortLabel,
	storeDataTimeLabel,
	storeDataValues,
	storeDataLoading,
	onTabChange,
	onDataStoreChange,
	onSearchChange,
	onToggleSearch,
	onOpenFavorites,
	onStoreFavorite,
	favoriteStoreIds,
	favoritePendingStoreIds,
	onOpenOrganization,
	onOpenFilter,
	onOpenSort,
	onOpenStoreDataOrganization,
	onOpenStoreDataSort,
	onOpenStoreDataFilter,
	onOpenPhone,
	onOpenStore,
	onOpenMultiView,
}: {
	activeTab: ApacStoreTab;
	videoRows: StoreVideoRow[];
	dataRows: StoreVideoRow[];
	storesLoading: boolean;
	onlineRate: number | null;
	selectedDataStoreId: string;
	search: string;
	searchOpen: boolean;
	filterActiveCount: number;
	organizationLabel: string;
	sortLabel: string;
	storeDataMetric: ApacStoreDataMetricKey;
	storeDataMetricLabel: string;
	storeDataSortLabel: string;
	storeDataTimeLabel: string;
	storeDataValues: Record<string, number | null>;
	storeDataLoading: boolean;
	onTabChange: (tab: ApacStoreTab) => void;
	onDataStoreChange: (storeId: string) => void;
	onSearchChange: (value: string) => void;
	onToggleSearch: () => void;
	onOpenFavorites: () => void;
	onStoreFavorite: (storeId: string) => void;
	favoriteStoreIds: string[];
	favoritePendingStoreIds: string[];
	onOpenOrganization: () => void;
	onOpenFilter: () => void;
	onOpenSort: () => void;
	onOpenStoreDataOrganization: () => void;
	onOpenStoreDataSort: () => void;
	onOpenStoreDataFilter: () => void;
	onOpenPhone: (store: Store) => void;
	onOpenStore: (store: Store, focusOffline?: boolean) => void;
	onOpenMultiView: (store: Store) => void;
}) {
	const { t, i18n } = useTranslation();
	const rateText = onlineRate == null ? '--' : `${onlineRate}%`;
	const summaryText = t(videoRows.length === 1 ? 'store_video.summary_one' : 'store_video.summary', {
		count: videoRows.length,
		rate: rateText,
	});
	const selectedDataRow = dataRows.find(({ store }) => String(store.store_id) === String(selectedDataStoreId)) ?? dataRows[0];

	return (
		<>
			<MobileRootPage style={{ background: '#fff', padding: '0 0 calc(18px + env(safe-area-inset-bottom))', overflowX: 'hidden' }}>
				<div style={{ padding: '14px 12px 0', boxSizing: 'border-box' }}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
					<div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
						<button
							type="button"
							onClick={() => onTabChange('video')}
							style={topTabStyle(activeTab === 'video')}
						>
							{t('store_video.tab_video')}
						</button>
						<button
							type="button"
							onClick={() => onTabChange('data')}
							style={{ ...topTabStyle(activeTab === 'data'), marginLeft: 18 }}
						>
							{t('store_video.tab_data')}
						</button>
					</div>
					<button
						type="button"
						aria-label={t('store_video.favorite_only')}
						onClick={onOpenFavorites}
						style={{ ...iconButtonStyle(true), marginLeft: 8 }}
					>
						<StarFilled />
					</button>
					<button
						type="button"
						aria-label={t('store_video.search')}
						onClick={onToggleSearch}
						style={{ ...iconButtonStyle(searchOpen), marginLeft: 2 }}
					>
						<SearchOutlined />
					</button>
				</div>

				{activeTab === 'data' ? (
					<ApacStoreDataPanel
						rows={dataRows}
						storesLoading={storesLoading}
						selectedStoreId={selectedDataRow?.store.store_id ?? ''}
						onStoreChange={onDataStoreChange}
						favoriteStoreIds={favoriteStoreIds}
						favoritePendingStoreIds={favoritePendingStoreIds}
						metric={storeDataMetric}
						metricLabel={storeDataMetricLabel}
						sortLabel={storeDataSortLabel}
						timeLabel={storeDataTimeLabel}
						metricValues={storeDataValues}
						metricLoading={storeDataLoading}
						organizationLabel={organizationLabel}
						onStoreFavorite={onStoreFavorite}
						onOpenOrganization={onOpenStoreDataOrganization}
						onOpenSort={onOpenStoreDataSort}
						onOpenFilter={onOpenStoreDataFilter}
						onOpenPhone={onOpenPhone}
						onOpenStore={onOpenStore}
					/>
				) : (
					<>

				<div style={{ display: 'grid', gridTemplateColumns: '1.25fr 0.75fr 0.9fr', gap: 4, marginTop: 18 }}>
					<StoreControlButton label={organizationLabel} onClick={onOpenOrganization} />
					<StoreControlButton
						label={t('store_video.filter')}
						badge={filterActiveCount > 0 ? filterActiveCount : undefined}
						onClick={onOpenFilter}
					/>
					<StoreControlButton label={sortLabel} onClick={onOpenSort} />
				</div>

				<div style={{ margin: '24px 0 18px', color: '#30343b', fontSize: 14, lineHeight: 1.35 }}>
					{summaryText}
				</div>

				<div style={{ display: 'grid', gap: 12 }}>
					{storesLoading && <Spin />}
					{!storesLoading && videoRows.length === 0 && <Empty description={t('native.common.no_data')} />}
					{videoRows.map(({ store, stats }) => (
						<Card
							key={store.store_id}
							style={{ borderRadius: 8, borderColor: 'transparent', boxShadow: '0 1px 10px rgba(15, 23, 42, 0.06)' }}
							styles={{ body: { padding: '16px 14px 16px' } }}
							onClick={() => onOpenStore(store, stats.offline > 0)}
						>
							<div style={{ display: 'flex', alignItems: 'flex-start' }}>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<ShopOutlined style={{ color: '#6b7280', fontSize: 20, marginRight: 8 }} />
										<Typography.Text style={{ color: '#20242b', fontSize: 15, fontWeight: 500, lineHeight: 1.2 }}>
											{store.store}
										</Typography.Text>
									</div>
								</div>
								<button
									type="button"
									aria-label={t('store_video.open_multi_view')}
									onClick={(e) => {
										e.stopPropagation();
										onOpenMultiView(store);
									}}
									style={{
										border: 0,
										background: 'transparent',
										color: PRIMARY,
										fontSize: 30,
										padding: 4,
										cursor: 'pointer',
										lineHeight: 1,
									}}
								>
									<AppstoreOutlined />
								</button>
							</div>

							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', alignItems: 'center', gap: 8, marginTop: 18 }}>
								<StoreMetric
									icon={<CloseCircleOutlined />}
									color="#ff4d4f"
									label={t('store_video.offline_count', { count: stats.offline })}
								/>
								<StoreMetric
									icon={<CheckCircleOutlined />}
									color="#15c450"
									label={t('store_video.online_count', { count: stats.online })}
								/>
								<StoreMetric
									icon={<VideoCameraOutlined />}
									color="#1890ff"
									label={t('store_video.total_count', { count: stats.total })}
								/>
							</div>

							<div style={{ display: 'flex', alignItems: 'center', color: '#9aa0a6', fontSize: 14, marginTop: 20, minWidth: 0 }}>
								<ClockCircleOutlined style={{ fontSize: 17, flex: '0 0 auto', marginRight: 6 }} />
								<span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('store_video.created_at')} {formatStoreCreateTime(store.create_time, i18n.language)}</span>
							</div>
						</Card>
					))}
				</div>
					</>
				)}
			</div>
			</MobileRootPage>
			{searchOpen && (
				<ApacStoreSearchPanel
					search={search}
					rows={activeTab === 'data' ? dataRows : videoRows}
					activeTab={activeTab}
					onSearchChange={onSearchChange}
					onCancel={onToggleSearch}
					onPick={(row) => {
						if (activeTab === 'data') {
							onDataStoreChange(row.store.store_id);
						} else {
							onOpenStore(row.store, row.stats.offline > 0);
						}
						onToggleSearch();
					}}
				/>
			)}
		</>
	);
}

function ApacStoreDataPanel({
	rows,
	storesLoading,
	selectedStoreId,
	onStoreChange,
	favoriteStoreIds,
	favoritePendingStoreIds = [],
	metric,
	metricLabel,
	sortLabel,
	timeLabel,
	metricValues,
	metricLoading,
	organizationLabel,
	controlLabels,
	controlHandlers,
	showOrganizationCard = true,
	emptyDescription,
	onStoreFavorite,
	onOpenOrganization,
	onOpenSort,
	onOpenFilter,
	onOpenPhone,
	onOpenStore,
}: {
	rows: StoreVideoRow[];
	storesLoading: boolean;
	selectedStoreId: string;
	onStoreChange: (storeId: string) => void;
	favoriteStoreIds: string[];
	favoritePendingStoreIds?: string[];
	metric: ApacStoreDataMetricKey;
	metricLabel: string;
	sortLabel: string;
	timeLabel: string;
	metricValues: Record<string, number | null>;
	metricLoading: boolean;
	organizationLabel: string;
	controlLabels?: [React.ReactNode, React.ReactNode, React.ReactNode];
	controlHandlers?: [() => void, () => void, () => void];
	showOrganizationCard?: boolean;
	emptyDescription?: React.ReactNode;
	onStoreFavorite: (storeId: string) => void;
	onOpenOrganization: () => void;
	onOpenSort: () => void;
	onOpenFilter: () => void;
	onOpenPhone: (store: Store) => void;
	onOpenStore: (store: Store, focusOffline?: boolean) => void;
}) {
	const { t } = useTranslation();
	const labels = controlLabels ?? [
		organizationLabel,
		sortLabel,
		t('native.common.filter'),
	];
	const handlers = controlHandlers ?? [
		onOpenOrganization,
		onOpenSort,
		onOpenFilter,
	];
	return (
		<div style={{ marginTop: 18 }}>
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr 0.85fr', gap: 4, color: mobileColor.textMuted, fontSize: mobileFontSize.bodySmall, marginBottom: 16 }}>
				<StoreControlButton label={labels[0]} onClick={handlers[0]} />
				<StoreControlButton label={labels[1]} onClick={handlers[1]} />
				<StoreControlButton label={labels[2]} onClick={handlers[2]} />
			</div>
			{showOrganizationCard && (
				<div style={{
					border: '1px solid #eef0f3',
					borderRadius: mobileRadius.md,
					padding: '12px 14px',
					marginBottom: 12,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					color: '#30343b',
					background: mobileColor.surface,
					boxShadow: mobileShadow.soft,
				}}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
						<ShopOutlined style={{ color: '#7c8490', fontSize: 18 }} />
						<span style={{ fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
							{organizationLabel || t('native.common.organization')}
						</span>
					</div>
					<span style={{ color: '#7c8490', fontSize: 14 }}>{t('native.common.level')}</span>
				</div>
			)}
			{storesLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spin /></div>}
			{!storesLoading && rows.length === 0 && (
				<Empty description={emptyDescription ?? t('native.common.no_data', { defaultValue: 'No data' })} style={{ marginTop: 32 }} />
			)}
			<div style={{ display: 'grid', gap: 10 }}>
				{rows.map(({ store }) => {
					const selected = String(store.store_id) === String(selectedStoreId);
					const storeId = String(store.store_id);
					const isFavorite = favoriteStoreIds.map(String).includes(storeId);
					const favoritePending = favoritePendingStoreIds.map(String).includes(storeId);
					const metricValue = metricValues[store.store_id];
					const metricText = metricLoading && metricValue == null ? '...' : formatStoreDataMetricValue(metricValue, metric);
					const metricUnit = getStoreDataUnitLabel(metric, t);
					const pickStore = () => onStoreChange(store.store_id);
					return (
						<div
							key={store.store_id}
							role="button"
							tabIndex={0}
							data-testid="store-data-row"
							data-store-id={store.store_id}
							onClick={pickStore}
							onKeyDown={(event) => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault();
									pickStore();
								}
							}}
							style={{
								border: selected ? `1px solid ${PRIMARY}` : '1px solid #eef0f3',
								background: '#fff',
								borderRadius: mobileRadius.md,
								padding: '14px',
								boxShadow: selected ? '0 0 0 2px rgba(255, 112, 67, 0.10)' : mobileShadow.soft,
								textAlign: 'left',
								cursor: 'pointer',
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
								<ShopOutlined style={{ color: '#7c8490', fontSize: 20, flex: '0 0 auto' }} />
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ color: mobileColor.text, fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
										{store.store}
									</div>
									<div style={{ color: mobileColor.textSubtle, marginTop: 8, fontSize: mobileFontSize.bodySmall }}>
										<span>{metricLabel}</span>
										<span style={{ color: PRIMARY, fontSize: mobileFontSize.topBar, fontWeight: mobileFontWeight.semibold, margin: '0 8px' }}>{metricText}</span>
										{metricUnit && <span>{metricUnit}</span>}
										<span style={{ display: 'block', color: '#b0b6bd', marginTop: 2, fontSize: 12 }}>{timeLabel}</span>
									</div>
								</div>
								<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 28px)', gap: 6, alignItems: 'center' }}>
									<button
										type="button"
										aria-label={t('store_video.favorite_only')}
										disabled={favoritePending}
										onClick={(event) => {
											event.stopPropagation();
											onStoreFavorite(storeId);
										}}
										style={nativeStoreDataIconStyle(isFavorite, favoritePending)}
									>
										{isFavorite ? <StarFilled /> : <StarOutlined />}
									</button>
									<button
										type="button"
										aria-label={t('native.common.phone', { defaultValue: 'Phone' })}
										onClick={(event) => {
											event.stopPropagation();
											onOpenPhone(store);
										}}
										style={nativeStoreDataIconStyle(false)}
									>
										<PhoneOutlined />
									</button>
									<button
										type="button"
										aria-label={t('native.common.video', { defaultValue: 'Video' })}
										onClick={(event) => {
											event.stopPropagation();
											onOpenStore(store);
										}}
										style={nativeStoreDataIconStyle(false)}
									>
										<VideoCameraOutlined />
									</button>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function ApacStoreSearchPanel({
	search,
	rows,
	activeTab,
	onSearchChange,
	onCancel,
	onPick,
}: {
	search: string;
	rows: StoreVideoRow[];
	activeTab: ApacStoreTab;
	onSearchChange: (value: string) => void;
	onCancel: () => void;
	onPick: (row: StoreVideoRow) => void;
}) {
	const { t, i18n } = useTranslation();
	const query = search.trim().toLocaleLowerCase(normalizeLocale(i18n.language));
	const results = query
		? rows.filter(({ store }) => {
			const code = store.store_code ?? '';
			return store.store.toLocaleLowerCase(normalizeLocale(i18n.language)).includes(query)
				|| code.toLocaleLowerCase(normalizeLocale(i18n.language)).includes(query);
		})
		: [];
	return (
		<div style={{
			position: 'fixed',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			zIndex: 1300,
			background: '#fff',
			padding: 'calc(12px + env(safe-area-inset-top)) 12px 24px',
			boxSizing: 'border-box',
		}}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
				<Input
					autoFocus
					prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
					allowClear
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
					placeholder={t('native.common.search_by_store_name')}
					size="large"
					style={{ flex: 1, borderRadius: 6, background: '#f4f5f7' }}
				/>
				<button
					type="button"
					onClick={onCancel}
					style={{ border: 0, background: 'transparent', color: '#4b5563', fontSize: mobileFontSize.body, padding: '8px 0' }}
				>
					{t('native.common.cancel')}
				</button>
			</div>
			{query ? (
				<div style={{ marginTop: 18, display: 'grid', gap: 10 }}>
					{results.length === 0 && <Empty description={t('native.common.no_data', { defaultValue: 'No data' })} />}
					{results.map((row) => (
						<button
							key={row.store.store_id}
							type="button"
							onClick={() => onPick(row)}
							style={{
								border: '1px solid #eef0f3',
								background: '#fff',
								borderRadius: mobileRadius.md,
								padding: '12px',
								display: 'flex',
								alignItems: 'center',
								gap: 10,
								textAlign: 'start',
								cursor: 'pointer',
							}}
						>
							<ShopOutlined style={{ color: PRIMARY, fontSize: 20 }} />
							<span style={{ flex: 1, minWidth: 0 }}>
								<span style={{ display: 'block', color: mobileColor.text, fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{row.store.store}
								</span>
								<span style={{ display: 'block', color: '#9aa0a6', fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{activeTab === 'data'
										? t('store_video.tab_data', { defaultValue: 'Store data' })
										: t('store_video.tab_video', { defaultValue: 'Store video' })}
									{row.store.store_code ? ` 路 ${row.store.store_code}` : ''}
								</span>
							</span>
							<RightOutlined style={{ color: '#c4c9d1', fontSize: 13 }} />
						</button>
					))}
				</div>
			) : (
				<div style={{ marginTop: 22, color: mobileColor.text, fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium }}>{t('native.common.search_history')}</div>
			)}
		</div>
	);
}

function ApacFavoriteStoreView({
	activeTab,
	videoRows,
	dataRows,
	storesLoading,
	selectedDataStoreId,
	favoriteStatus,
	storeDataMetric,
	storeDataMetricLabel,
	storeDataSortLabel,
	storeDataTimeLabel,
	storeDataValues,
	storeDataLoading,
	favoriteStoreIds,
	favoritePendingStoreIds,
	onBack,
	onTabChange,
	onFavoriteStatusChange,
	onDataStoreChange,
	onOpenFavoriteDataMetric,
	onOpenFavoriteDataSort,
	onOpenFavoriteDataTime,
	onStoreFavorite,
	onOpenPhone,
	onOpenStore,
	onOpenMultiView,
}: {
	activeTab: ApacStoreTab;
	videoRows: StoreVideoRow[];
	dataRows: StoreVideoRow[];
	storesLoading: boolean;
	selectedDataStoreId: string;
	favoriteStatus: FavoriteStatusFilter;
	storeDataMetric: ApacStoreDataMetricKey;
	storeDataMetricLabel: string;
	storeDataSortLabel: string;
	storeDataTimeLabel: string;
	storeDataValues: Record<string, number | null>;
	storeDataLoading: boolean;
	favoriteStoreIds: string[];
	favoritePendingStoreIds: string[];
	onBack: () => void;
	onTabChange: (tab: ApacStoreTab) => void;
	onFavoriteStatusChange: (status: FavoriteStatusFilter) => void;
	onDataStoreChange: (storeId: string) => void;
	onOpenFavoriteDataMetric: () => void;
	onOpenFavoriteDataSort: () => void;
	onOpenFavoriteDataTime: () => void;
	onStoreFavorite: (storeId: string) => void;
	onOpenPhone: (store: Store) => void;
	onOpenStore: (store: Store, focusOffline?: boolean) => void;
	onOpenMultiView: (store: Store) => void;
}) {
	const { t } = useTranslation();
	const noCollection = t('store_video.no_collection');
	const selectedDataRow = dataRows.find(({ store }) => String(store.store_id) === String(selectedDataStoreId)) ?? dataRows[0];
	const statusItems: Array<{ key: FavoriteStatusFilter; label: string }> = [
		{ key: 'all', label: t('native.common.all_status') },
		{ key: 'online', label: t('native.common.online') },
		{ key: 'offline', label: t('native.common.offline') },
	];
	return (
		<MobileRootPage style={{ background: '#fff', padding: '0 0 calc(18px + env(safe-area-inset-bottom))', overflowX: 'hidden' }}>
			<div style={{ padding: 'calc(8px + env(safe-area-inset-top)) 12px 0', boxSizing: 'border-box' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					<button
						type="button"
						aria-label={t('native.common.back', { defaultValue: 'Back' })}
						onClick={onBack}
						style={{
							border: 0,
							background: 'transparent',
							color: PRIMARY,
							fontSize: 25,
							padding: 0,
							width: 28,
							height: 44,
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: 'pointer',
						}}
					>
						<LeftOutlined />
					</button>
					<div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
						<button
							type="button"
							onClick={() => onTabChange('video')}
							style={topTabStyle(activeTab === 'video')}
						>
							{t('store_video.favorite_store_video')}
						</button>
						<button
							type="button"
							onClick={() => onTabChange('data')}
							style={{ ...topTabStyle(activeTab === 'data'), marginLeft: 18 }}
						>
							{t('store_video.favorite_store_data')}
						</button>
					</div>
				</div>
				{activeTab === 'data' ? (
					<ApacStoreDataPanel
						rows={dataRows}
						storesLoading={storesLoading}
						selectedStoreId={selectedDataRow?.store.store_id ?? ''}
						onStoreChange={onDataStoreChange}
						favoriteStoreIds={favoriteStoreIds}
						favoritePendingStoreIds={favoritePendingStoreIds}
						metric={storeDataMetric}
						metricLabel={storeDataMetricLabel}
						sortLabel={storeDataSortLabel}
						timeLabel={storeDataTimeLabel}
						metricValues={storeDataValues}
						metricLoading={storeDataLoading}
						organizationLabel=""
						controlLabels={[storeDataMetricLabel, storeDataSortLabel, storeDataTimeLabel]}
						controlHandlers={[onOpenFavoriteDataMetric, onOpenFavoriteDataSort, onOpenFavoriteDataTime]}
						showOrganizationCard={false}
						emptyDescription={noCollection}
						onStoreFavorite={onStoreFavorite}
						onOpenOrganization={onOpenFavoriteDataMetric}
						onOpenSort={onOpenFavoriteDataSort}
						onOpenFilter={onOpenFavoriteDataTime}
						onOpenPhone={onOpenPhone}
						onOpenStore={onOpenStore}
					/>
				) : (
					<>
						<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, marginTop: 12 }}>
							{statusItems.map((item) => {
								const active = favoriteStatus === item.key;
								return (
								<button
									key={item.key}
									type="button"
									onClick={() => onFavoriteStatusChange(item.key)}
									style={{
										border: 0,
										borderBottom: active ? `3px solid ${PRIMARY}` : '3px solid transparent',
										background: 'transparent',
										color: active ? PRIMARY : '#555b63',
										fontSize: 17,
										padding: '10px 0 12px',
										cursor: 'pointer',
									}}
								>
									{item.label}
								</button>
								);
							})}
						</div>
						<div style={{ display: 'grid', gap: 12, marginTop: 18 }}>
							{storesLoading && <Spin />}
							{!storesLoading && videoRows.length === 0 && <Empty description={noCollection} style={{ marginTop: 110 }} />}
							{videoRows.map((row) => (
								<FavoriteVideoStoreGroup
									key={row.store.store_id}
									row={row}
									status={favoriteStatus}
									favoritePending={favoritePendingStoreIds.map(String).includes(String(row.store.store_id))}
									onStoreFavorite={onStoreFavorite}
									onOpenStore={onOpenStore}
									onOpenPhone={onOpenPhone}
									onOpenMultiView={onOpenMultiView}
								/>
							))}
						</div>
					</>
				)}
			</div>
		</MobileRootPage>
	);
}

function FavoriteVideoStoreGroup({
	row,
	status,
	favoritePending,
	onStoreFavorite,
	onOpenStore,
	onOpenPhone,
	onOpenMultiView,
}: {
	row: StoreVideoRow;
	status: FavoriteStatusFilter;
	favoritePending: boolean;
	onStoreFavorite: (storeId: string) => void;
	onOpenStore: (store: Store, focusOffline?: boolean) => void;
	onOpenPhone: (store: Store) => void;
	onOpenMultiView: (store: Store) => void;
}) {
	const { t } = useTranslation();
	const { store } = row;
	const devices = getFavoriteVisibleDevices(row, status);
	return (
		<div style={{ background: '#fff', borderBottom: '1px solid #edf0f3' }}>
			<button
				type="button"
				onClick={() => onOpenStore(store, status === 'offline' || row.stats.offline > 0)}
				style={{
					width: '100%',
					border: 0,
					background: '#fff',
					display: 'flex',
					alignItems: 'center',
					padding: '18px 0 14px',
					color: '#30343b',
					cursor: 'pointer',
					textAlign: 'left',
				}}
			>
				<ShopOutlined style={{ color: '#666b73', fontSize: 22, marginRight: 10, flex: '0 0 auto' }} />
				<span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>
					{store.store}
				</span>
				<RightOutlined style={{ color: '#b8bdc5', fontSize: 24, flex: '0 0 auto' }} />
			</button>
			<div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '0 0 14px 44px' }}>
				<button
					type="button"
					aria-label={t('store_video.favorite_only')}
					disabled={favoritePending}
					onClick={() => onStoreFavorite(store.store_id)}
					style={favoriteVideoActionStyle(favoritePending)}
				>
					<StarFilled />
				</button>
				<button
					type="button"
					aria-label={t('native.common.phone')}
					onClick={() => onOpenPhone(store)}
					style={favoriteVideoActionStyle(false)}
				>
					<PhoneOutlined />
				</button>
				<button
					type="button"
					aria-label={t('store_video.open_multi_view')}
					onClick={() => onOpenMultiView(store)}
					style={favoriteVideoActionStyle(false)}
				>
					<AppstoreOutlined />
				</button>
			</div>
			<div style={{ borderTop: devices.length > 0 ? '1px solid #edf0f3' : 0 }}>
				{devices.map((device) => (
					<FavoriteVideoDeviceRow
						key={`${String(device.device_id)}:${String(device.channel_id ?? '')}:${String(device.sn ?? '')}`}
						device={device}
						store={store}
						onOpenStore={onOpenStore}
					/>
				))}
			</div>
		</div>
	);
}

function FavoriteVideoDeviceRow({
	device,
	store,
	onOpenStore,
}: {
	device: DeviceLite;
	store: Store;
	onOpenStore: (store: Store, focusOffline?: boolean) => void;
}) {
	const online = String(device.status) === '1';
	return (
		<button
			type="button"
			onClick={() => onOpenStore(store, !online)}
			style={{
				width: '100%',
				minHeight: 56,
				border: 0,
				borderBottom: '1px solid #edf0f3',
				background: '#fff',
				display: 'flex',
				alignItems: 'center',
				padding: '12px 0 12px 44px',
				color: online ? '#30343b' : '#c7ccd3',
				cursor: 'pointer',
				textAlign: 'left',
			}}
		>
			<VideoCameraOutlined style={{ color: online ? '#58a9df' : '#d5d9de', fontSize: 25, marginRight: 14, flex: '0 0 auto' }} />
			<span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>
				{deviceLabel(device)}
			</span>
			<RightOutlined style={{ color: '#b8bdc5', fontSize: 24, marginRight: 8, flex: '0 0 auto' }} />
		</button>
	);
}

function favoriteVideoActionStyle(disabled: boolean): React.CSSProperties {
	return {
		border: 0,
		background: 'transparent',
		color: PRIMARY,
		fontSize: 28,
		padding: 4,
		cursor: disabled ? 'default' : 'pointer',
		lineHeight: 1,
		opacity: disabled ? 0.55 : 1,
	};
}

function nativeStoreDataIconStyle(active: boolean, disabled = false): React.CSSProperties {
	return {
		border: 0,
		background: 'transparent',
		color: active ? PRIMARY : '#7c8490',
		fontSize: 20,
		padding: 3,
		cursor: disabled ? 'default' : 'pointer',
		lineHeight: 1,
		opacity: disabled ? 0.55 : 1,
	};
}

function StoreControlButton({
	label,
	badge,
	onClick,
}: {
	label: React.ReactNode;
	badge?: number;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				border: 0,
				background: 'transparent',
				color: '#666b73',
				minHeight: 38,
				fontSize: 15,
				padding: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				cursor: 'pointer',
				minWidth: 0,
				outline: 'none',
				WebkitTapHighlightColor: 'transparent',
			}}
		>
			<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
			<DownOutlined style={{ color: '#a0a4aa', fontSize: 12, flex: '0 0 auto', marginLeft: 4 }} />
			{badge ? (
				<span style={{
					minWidth: 18,
					height: 18,
					borderRadius: 9,
					background: PRIMARY,
					color: '#fff',
					fontSize: 11,
					lineHeight: '18px',
					textAlign: 'center',
					padding: '0 5px',
					flex: '0 0 auto',
				}}>
					{badge}
				</span>
			) : null}
		</button>
	);
}

function StoreMetric({ icon, color, label }: { icon: React.ReactNode; color: string; label: React.ReactNode }) {
	return (
		<div style={{ display: 'flex', alignItems: 'center', color: '#333840', fontSize: 14, lineHeight: 1.1, minWidth: 0 }}>
			<span style={{ color, fontSize: 17, display: 'inline-flex', flex: '0 0 auto', marginRight: 4 }}>{icon}</span>
			<span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
		</div>
	);
}

function MultiViewStoreDrawer({
	open,
	stores,
	loading,
	onClose,
	onPick,
}: {
	open: boolean;
	stores: Array<{ store: Store; online: number; total: number }>;
	loading: boolean;
	onClose: () => void;
	onPick: (store: Store) => void;
}) {
	const { t } = useTranslation();
	return (
		<Drawer
			placement="bottom"
			height="72%"
			open={open}
			onClose={onClose}
			title={t('root.monitor.multi_view_select_store')}
			styles={{ body: { padding: '10px 12px 24px' } }}
		>
			{loading && <Spin />}
			{!loading && stores.length === 0 && <Empty description={t('common.no_data')} />}
			{!loading && stores.length > 0 && (
				<div style={{ display: 'grid', gap: 10 }}>
					{stores.map(({ store, online, total }) => {
						const disabled = online === 0;
						return (
							<button
								key={store.store_id}
								type="button"
								disabled={disabled}
								onClick={() => onPick(store)}
								style={{
									border: '1px solid #e5e7eb',
									background: '#fff',
									borderRadius: 8,
									padding: '12px',
									textAlign: 'start',
									display: 'flex',
									alignItems: 'center',
									gap: 10,
									cursor: disabled ? 'not-allowed' : 'pointer',
									opacity: disabled ? 0.58 : 1,
								}}
							>
								<VideoCameraOutlined style={{ color: disabled ? '#9aa3af' : PRIMARY, fontSize: 22 }} />
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ color: '#111827', fontSize: 15, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
										{store.store}
									</div>
									<div style={{ color: '#697386', fontSize: 12, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
										{store.store_code || store.group_name || store.addr || '—'}
									</div>
								</div>
								<Tag color={online > 0 ? 'success' : 'default'} style={{ marginInlineEnd: 0 }}>
									{online > 0
										? t('root.monitor.multi_view_store_count', { online, total })
										: t('root.monitor.multi_view_no_online')}
								</Tag>
							</button>
						);
					})}
				</div>
			)}
		</Drawer>
	);
}

/** 筛选 Drawer — 复刻 iOS 截图（门店设备/设备状态/收藏状态/门店排序） */
function OrganizationDrawer({
	open,
	loading,
	value,
	groups,
	onClose,
	onPick,
}: {
	open: boolean;
	loading: boolean;
	value: string;
	groups: Array<{ id: string; name: string; storeCount?: number }>;
	onClose: () => void;
	onPick: (id: string) => void;
}) {
	const { t } = useTranslation();
	return (
		<Drawer
			placement="bottom"
			height="72%"
			open={open}
			onClose={onClose}
			title={t('store_video.organization')}
			styles={{ body: { padding: '10px 12px 24px' } }}
		>
			{loading && <Spin />}
			<div style={{ display: 'grid', gap: 10 }}>
				<DrawerPickRow
					active={value === 'all'}
					title={t('store_video.organization_all')}
					onClick={() => onPick('all')}
				/>
				{!loading && groups.length === 0 && (
					<Typography.Text type="secondary" style={{ padding: '6px 2px' }}>
						{t('store_video.organization_empty')}
					</Typography.Text>
				)}
				{groups.map((group) => (
					<DrawerPickRow
						key={group.id}
						active={value === group.id}
						title={group.name}
						subtitle={group.storeCount != null ? t('store_video.organization_store_count', { count: group.storeCount }) : undefined}
						onClick={() => onPick(group.id)}
					/>
				))}
			</div>
		</Drawer>
	);
}

function SortDrawer({
	open,
	value,
	onClose,
	onPick,
}: {
	open: boolean;
	value: ApacSortKey;
	onClose: () => void;
	onPick: (key: ApacSortKey) => void;
}) {
	const { t } = useTranslation();
	const items: Array<{ key: ApacSortKey; title: string }> = [
		{ key: 'default', title: t('store_video.sort_default') },
		{ key: 'create_desc', title: t('store_video.sort_create') },
		{ key: 'name_az', title: t('store_video.sort_name') },
	];
	return (
		<ApacBottomSheet
			open={open}
			onClose={onClose}
			height="36vh"
			bodyStyle={{ padding: '12px 0 calc(20px + env(safe-area-inset-bottom))' }}
		>
			<div style={{ display: 'grid' }}>
				{items.map((item) => (
					<DrawerPickRow
						key={item.key}
						active={value === item.key}
						title={item.title}
						onClick={() => onPick(item.key)}
					/>
				))}
			</div>
		</ApacBottomSheet>
	);
}

function StoreDataSortSheet({
	open,
	value,
	includeDefault = false,
	onClose,
	onPick,
}: {
	open: boolean;
	value: StoreDataSortKey;
	includeDefault?: boolean;
	onClose: () => void;
	onPick: (key: StoreDataSortKey) => void;
}) {
	const { t } = useTranslation();
	const items: Array<{ key: StoreDataSortKey; title: string }> = [
		...(includeDefault ? [{ key: 'default_ranking' as const, title: getStoreDataSortLabel('default_ranking', t) }] : []),
		{ key: 'high_to_low', title: getStoreDataSortLabel('high_to_low', t) },
		{ key: 'low_to_high', title: getStoreDataSortLabel('low_to_high', t) },
	];
	return (
		<ApacBottomSheet
			open={open}
			onClose={onClose}
			height="28vh"
			bodyStyle={{ padding: '18px 0 calc(20px + env(safe-area-inset-bottom))' }}
		>
			<div style={{ display: 'grid' }}>
				{items.map((item) => (
					<DrawerPickRow
						key={item.key}
						active={value === item.key}
						title={item.title}
						onClick={() => onPick(item.key)}
					/>
				))}
			</div>
		</ApacBottomSheet>
	);
}

function StoreDataMetricSheet({
	open,
	value,
	onClose,
	onPick,
}: {
	open: boolean;
	value: ApacStoreDataMetricKey;
	onClose: () => void;
	onPick: (key: ApacStoreDataMetricKey) => void;
}) {
	const { t } = useTranslation();
	const items: ApacStoreDataMetricKey[] = ['turnover_rate', 'entry_number', 'total_number', 'total_sales', 'ecr', 'events'];
	return (
		<ApacBottomSheet
			open={open}
			onClose={onClose}
			height="46vh"
			bodyStyle={{ padding: '14px 0 calc(20px + env(safe-area-inset-bottom))' }}
		>
			<div style={{ display: 'grid' }}>
				{items.map((item) => (
					<DrawerPickRow
						key={item}
						active={value === item}
						title={getStoreDataMetricLabel(item, t)}
						onClick={() => onPick(item)}
					/>
				))}
			</div>
		</ApacBottomSheet>
	);
}

function StoreDataTimeSheet({
	open,
	value,
	includeToday = true,
	onClose,
	onPick,
}: {
	open: boolean;
	value: StoreDataTimeKey;
	includeToday?: boolean;
	onClose: () => void;
	onPick: (key: StoreDataTimeKey) => void;
}) {
	const { t } = useTranslation();
	const items: StoreDataTimeKey[] = includeToday
		? ['today', 'yesterday', 'week', 'seven', 'month', 'thirty']
		: ['yesterday', 'week', 'seven', 'month', 'thirty'];
	return (
		<ApacBottomSheet
			open={open}
			onClose={onClose}
			height={includeToday ? '46vh' : '40vh'}
			bodyStyle={{ padding: '14px 0 calc(20px + env(safe-area-inset-bottom))' }}
		>
			<div style={{ display: 'grid' }}>
				{items.map((item) => (
					<DrawerPickRow
						key={item}
						active={value === item}
						title={getStoreDataTimeLabel(item, t)}
						onClick={() => onPick(item)}
					/>
				))}
			</div>
		</ApacBottomSheet>
	);
}

function StoreDataFilterSheet({
	open,
	metric,
	time,
	onClose,
	onApply,
}: {
	open: boolean;
	metric: ApacStoreDataMetricKey;
	time: StoreDataTimeKey;
	onClose: () => void;
	onApply: (metric: ApacStoreDataMetricKey, time: StoreDataTimeKey) => void;
}) {
	const { t } = useTranslation();
	const [draftMetric, setDraftMetric] = useState<ApacStoreDataMetricKey>(metric);
	const [draftTime, setDraftTime] = useState<StoreDataTimeKey>(time);
	useEffect(() => {
		if (open) {
			setDraftMetric(metric);
			setDraftTime(time);
		}
	}, [metric, open, time]);
	const metrics: ApacStoreDataMetricKey[] = ['entry_number', 'total_number', 'ecr', 'total_sales', 'turnover_rate', 'events'];
	const times: StoreDataTimeKey[] = ['today', 'yesterday', 'seven', 'week', 'month', 'thirty'];
	return (
		<ApacBottomSheet
			open={open}
			onClose={onClose}
			height="50vh"
			bodyStyle={{ padding: '18px 16px calc(18px + env(safe-area-inset-bottom))' }}
		>
			<Section title={t('store_video.filter_data')}>
				{metrics.map((item) => (
					<Chip
						key={item}
						active={draftMetric === item}
						native
						onClick={() => setDraftMetric(item)}
					>
						{getStoreDataMetricLabel(item, t)}
					</Chip>
				))}
			</Section>
			<Section title={t('store_video.filter_time')}>
				{times.map((item) => (
					<Chip
						key={item}
						active={draftTime === item}
						native
						onClick={() => setDraftTime(item)}
					>
						{getStoreDataTimeLabel(item, t)}
					</Chip>
				))}
			</Section>
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
				<Button
					size="large"
					onClick={onClose}
					style={{ height: 44, borderRadius: 4, borderColor: PRIMARY, color: PRIMARY }}
				>
					{t('native.common.cancel')}
				</Button>
				<Button
					type="primary"
					size="large"
					onClick={() => onApply(draftMetric, draftTime)}
					style={{ height: 44, borderRadius: 4, background: PRIMARY, borderColor: PRIMARY }}
				>
					{t('native.common.confirm')}
				</Button>
			</div>
		</ApacBottomSheet>
	);
}

function ApacPhoneSheet({ store, onClose }: { store: Store | null; onClose: () => void }) {
	const { t } = useTranslation();
	const phones = store ? getStorePhones(store) : [];
	return (
		<ApacBottomSheet
			open={!!store}
			onClose={onClose}
			height="230px"
			bodyStyle={{ padding: 0 }}
		>
			<div style={{ textAlign: 'center', color: mobileColor.textSubtle, fontSize: mobileFontSize.title, padding: '18px 12px' }}>
				{t('store_video.calling_store_manager')}
			</div>
			<div style={{ height: 1, background: '#eef0f3' }} />
			{phones.length > 0 ? phones.map((phone) => (
				<a
					key={phone}
					href={`tel:${phone}`}
					style={{
						display: 'block',
						textAlign: 'center',
						textDecoration: 'none',
						color: PRIMARY,
						fontSize: mobileFontSize.topBar,
						fontWeight: mobileFontWeight.medium,
						padding: '18px 12px',
						background: '#fff',
					}}
				>
					{phone}
				</a>
			)) : (
				<div style={{ textAlign: 'center', color: '#9aa0a6', padding: '18px 12px' }}>
					{t('native.common.no_data')}
				</div>
			)}
			<div style={{ height: 8, background: '#f4f5f7' }} />
			<button
				type="button"
				onClick={onClose}
				style={{
					width: '100%',
					border: 0,
					background: '#fff',
					color: '#4b5563',
					fontSize: mobileFontSize.topBar,
					padding: '18px 12px calc(18px + env(safe-area-inset-bottom))',
					cursor: 'pointer',
				}}
			>
				{t('native.common.cancel')}
			</button>
		</ApacBottomSheet>
	);
}

function DrawerPickRow({
	active,
	title,
	subtitle,
	onClick,
}: {
	active: boolean;
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				border: active ? `1px solid ${PRIMARY}` : '1px solid #e5e7eb',
				background: active ? '#fff7f2' : '#fff',
				color: '#111827',
				borderRadius: 8,
				padding: '13px 14px',
				textAlign: 'start',
				cursor: 'pointer',
			}}
		>
			<div style={{ fontSize: mobileFontSize.title, fontWeight: active ? mobileFontWeight.semibold : mobileFontWeight.medium }}>{title}</div>
			{subtitle && <div style={{ color: '#697386', fontSize: 12, marginTop: 4 }}>{subtitle}</div>}
		</button>
	);
}

function FilterDrawer({
	open,
	onClose,
	value,
	showSort = true,
	onApply,
}: {
	open: boolean;
	onClose: () => void;
	value: ActiveFilter;
	showSort?: boolean;
	onApply: (next: ActiveFilter) => void;
}) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState<ActiveFilter>(value);
	const native = !showSort;

	useEffect(() => { if (open) setDraft(value); }, [open, value]);
	const content = (
		<>
			<Section title={t('vfilter.device')}>
				<Chip active={draft.device === 'has'} native={native} onClick={() => setDraft((p) => ({ ...p, device: p.device === 'has'  ? 'any' : 'has'  }))}>{t('vfilter.has_dev')}</Chip>
				<Chip active={draft.device === 'none'} native={native} onClick={() => setDraft((p) => ({ ...p, device: p.device === 'none' ? 'any' : 'none' }))}>{t('vfilter.no_dev')}</Chip>
			</Section>

			<Section title={t('vfilter.status')}>
				<Chip active={draft.status === 'online'} native={native} onClick={() => setDraft((p) => ({ ...p, status: p.status === 'online'  ? 'any' : 'online'  }))}>{t('vfilter.online')}</Chip>
				<Chip active={draft.status === 'offline'} native={native} onClick={() => setDraft((p) => ({ ...p, status: p.status === 'offline' ? 'any' : 'offline' }))}>{t('vfilter.offline')}</Chip>
			</Section>

			<Section title={t('vfilter.fav_status')}>
				<Chip active={draft.fav === 'fav'} native={native} onClick={() => setDraft((p) => ({ ...p, fav: p.fav === 'fav'   ? 'any' : 'fav'   }))}>{t('vfilter.faved')}</Chip>
				<Chip active={draft.fav === 'unfav'} native={native} onClick={() => setDraft((p) => ({ ...p, fav: p.fav === 'unfav' ? 'any' : 'unfav' }))}>{t('vfilter.unfaved')}</Chip>
			</Section>

			{showSort && (
				<Section title={t('vfilter.sort')}>
					<Chip active={draft.sort === 'create'} onClick={() => setDraft((p) => ({ ...p, sort: 'create' }))}>{t('vfilter.sort_create')}</Chip>
					<Chip active={draft.sort === 'name'}   onClick={() => setDraft((p) => ({ ...p, sort: 'name' }))}>{t('vfilter.sort_name')}</Chip>
				</Section>
			)}

			<div style={{
				display: 'grid',
				gridTemplateColumns: '1fr 1.5fr',
				gap: 12,
				marginTop: 24,
			}}>
				<Button
					size="large"
					onClick={() => setDraft(DEFAULT_FILTER)}
					style={{ height: 48, borderRadius: native ? 2 : 24, borderColor: native ? '#e5e7eb' : PRIMARY, color: native ? '#30343b' : PRIMARY }}
				>
					{native ? t('native.common.reset') : t('vfilter.reset')}
				</Button>
				<Button
					type="primary"
					size="large"
					onClick={() => onApply(draft)}
					style={{ height: 48, borderRadius: native ? 2 : 24, background: PRIMARY, borderColor: PRIMARY }}
				>
					{native ? t('native.common.confirm') : t('vfilter.ok')}
				</Button>
			</div>
		</>
	);

	if (native) {
		return (
			<ApacBottomSheet
				open={open}
				onClose={onClose}
				height="68vh"
				bodyStyle={{ padding: '16px 16px calc(18px + env(safe-area-inset-bottom))' }}
			>
				{content}
			</ApacBottomSheet>
		);
	}

	return (
		<Drawer
			placement="bottom"
			height="80%"
			open={open}
			onClose={onClose}
			title={t('vfilter.title')}
			styles={{ body: { padding: '8px 16px 24px' } }}
		>
			{content}
		</Drawer>
	);
}

function ApacBottomSheet({
	open,
	onClose,
	height,
	bodyStyle,
	children,
}: {
	open: boolean;
	onClose: () => void;
	height: string;
	bodyStyle?: React.CSSProperties;
	children: React.ReactNode;
}) {
	const { t } = useTranslation();
	if (!open) return null;
	return (
		<div
			style={{
				position: 'fixed',
				top: 0,
				right: 0,
				bottom: 0,
				left: 0,
				zIndex: 1400,
				background: 'rgba(0, 0, 0, 0.18)',
			}}
		>
			<button
				type="button"
				aria-label={t('common.close', { defaultValue: t('native.common.cancel') })}
				onClick={onClose}
				style={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					left: 0,
					border: 0,
					background: 'transparent',
					padding: 0,
				}}
			/>
			<div
				style={{
					position: 'absolute',
					left: 0,
					right: 0,
					bottom: 0,
					height,
					maxHeight: '86vh',
					overflowY: 'auto',
					background: '#fff',
					boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.12)',
					...bodyStyle,
				}}
			>
				{children}
			</div>
		</div>
	);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div style={{ marginBottom: 18 }}>
			<div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>{title}</div>
			<div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>{children}</div>
		</div>
	);
}

function Chip({
	active,
	native = false,
	onClick,
	children,
}: {
	active: boolean;
	native?: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<span
			onClick={onClick}
			style={{
				padding: native ? '8px 18px' : '8px 22px',
				borderRadius: native ? 4 : 8,
				background: active ? '#FFF1E8' : native ? '#fff' : '#F5F5F7',
				color:      active ? PRIMARY    : '#333',
				border:     active ? `1px solid ${PRIMARY}` : native ? '1px solid #dfe3e8' : '1px solid transparent',
				fontSize: 14,
				cursor: 'pointer',
				userSelect: 'none',
				minWidth: 110,
				textAlign: 'center',
			}}
		>
			{children}
		</span>
	);
}

function topTabStyle(active: boolean): React.CSSProperties {
	return {
		border: 0,
		borderBottom: active ? `4px solid ${PRIMARY}` : '4px solid transparent',
		background: 'transparent',
		color: active ? PRIMARY : '#666b73',
		fontSize: 21,
		lineHeight: 1.15,
		padding: '10px 0 14px',
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
	};
}

function iconButtonStyle(active: boolean): React.CSSProperties {
	return {
		border: 0,
		background: 'transparent',
		color: active ? PRIMARY : '#ff7a1a',
		fontSize: 30,
		lineHeight: 1,
		padding: 2,
		cursor: 'pointer',
		outline: 'none',
		WebkitTapHighlightColor: 'transparent',
	};
}

function getStoreDataDateRange(key: StoreDataTimeKey): { startDate: string; endDate: string } {
	const today = dayjs();
	if (key === 'yesterday') {
		const day = today.subtract(1, 'day').format('YYYY-MM-DD');
		return { startDate: day, endDate: day };
	}
	if (key === 'week') {
		const mondayOffset = (today.day() + 6) % 7;
		return {
			startDate: today.subtract(mondayOffset, 'day').format('YYYY-MM-DD'),
			endDate: today.format('YYYY-MM-DD'),
		};
	}
	if (key === 'seven') {
		return {
			startDate: today.subtract(6, 'day').format('YYYY-MM-DD'),
			endDate: today.format('YYYY-MM-DD'),
		};
	}
	if (key === 'month') {
		return {
			startDate: today.startOf('month').format('YYYY-MM-DD'),
			endDate: today.format('YYYY-MM-DD'),
		};
	}
	if (key === 'thirty') {
		return {
			startDate: today.subtract(29, 'day').format('YYYY-MM-DD'),
			endDate: today.format('YYYY-MM-DD'),
		};
	}
	const day = today.format('YYYY-MM-DD');
	return { startDate: day, endDate: day };
}

function makeStoreDataCacheKey(metric: ApacStoreDataMetricKey, startDate: string, endDate: string): string {
	return `${metric}:${startDate}:${endDate}`;
}

function compareStoreDataRows(
	a: StoreVideoRow,
	b: StoreVideoRow,
	values: Record<string, number | null>,
	sort: StoreDataSortKey,
	locale: string,
): number {
	if (sort === 'default_ranking') return a.index - b.index;
	const av = numberOrUndefined(values[a.store.store_id]);
	const bv = numberOrUndefined(values[b.store.store_id]);
	if (av == null && bv == null) return a.store.store.localeCompare(b.store.store, locale) || a.index - b.index;
	if (av == null) return 1;
	if (bv == null) return -1;
	const diff = sort === 'high_to_low' ? bv - av : av - bv;
	return diff || a.store.store.localeCompare(b.store.store, locale) || a.index - b.index;
}

function numberOrUndefined(value: number | null | undefined): number | undefined {
	const numeric = Number(value);
	return value == null || Number.isNaN(numeric) ? undefined : numeric;
}

function getStoreDataMetricLabel(metric: ApacStoreDataMetricKey, t: ReturnType<typeof useTranslation>['t']): string {
	return t(`store_video.metric_${metric}`);
}

function getStoreDataTimeLabel(time: StoreDataTimeKey, t: ReturnType<typeof useTranslation>['t']): string {
	return t(`store_video.time_${time}`);
}

function getStoreDataSortLabel(sort: StoreDataSortKey, t: ReturnType<typeof useTranslation>['t']): string {
	return t(`store_video.sort_${sort}`);
}

function formatStoreDataMetricValue(value: number | null | undefined, metric: ApacStoreDataMetricKey): string {
	const numeric = numberOrUndefined(value);
	if (numeric == null) return '--';
	if (metric === 'ecr' || metric === 'turnover_rate') {
		const percent = Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
		return `${formatCompactNumber(percent)}%`;
	}
	return formatCompactNumber(numeric);
}

function formatCompactNumber(value: number): string {
	if (Number.isInteger(value)) return String(value);
	return String(Math.round(value * 100) / 100);
}

function getStoreDataUnitLabel(metric: ApacStoreDataMetricKey, t: ReturnType<typeof useTranslation>['t']): string {
	if (metric === 'entry_number' || metric === 'total_number') {
		return t('native.common.people', { defaultValue: 'People' });
	}
	return '';
}

function getStorePhones(store: Store): string[] {
	const candidates: string[] = [];
	for (const phone of [store.store_phone, store.shopowner_phone]) {
		if (phone) candidates.push(String(phone));
	}
	const more = store.more_phone;
	if (Array.isArray(more)) {
		candidates.push(...more.map(String));
	} else if (typeof more === 'string' && more.trim()) {
		const trimmed = more.trim();
		if (trimmed.startsWith('[')) {
			try {
				const parsed = JSON.parse(trimmed);
				if (Array.isArray(parsed)) candidates.push(...parsed.map(String));
			} catch {
				candidates.push(trimmed);
			}
		} else {
			candidates.push(...trimmed.split(/[,;|]/));
		}
	}
	const seen = new Set<string>();
	return candidates
		.map((phone) => phone.trim())
		.filter((phone) => {
			if (!phone || seen.has(phone)) return false;
			seen.add(phone);
			return true;
		});
}

function getFavoriteVisibleDevices(row: StoreVideoRow, status: FavoriteStatusFilter): DeviceLite[] {
	if (status === 'online') return row.devices.filter((device) => String(device.status) === '1');
	if (status === 'offline') return row.devices.filter((device) => String(device.status) !== '1');
	return row.devices;
}

function getStoreStats(devices: DeviceLite[] | undefined): StoreDeviceStats {
	const list = devices ?? [];
	const online = list.filter((device) => String(device.status) === '1').length;
	return {
		total: list.length,
		online,
		offline: Math.max(list.length - online, 0),
	};
}

function normalizeStoreGroups(groups: StoreGroup[] | null): Array<{ id: string; name: string; storeCount?: number }> {
	if (!groups) return [];
	const seen = new Set<string>();
	const normalized: Array<{ id: string; name: string; storeCount?: number }> = [];
	for (const group of groups) {
		const id = String(group.group_id ?? group.id ?? '').trim();
		if (!id || seen.has(id)) continue;
		seen.add(id);
		const name = String(group.group_name ?? group.name ?? id).trim();
		const rawCount = group.store_count;
		const storeCount = rawCount == null || Number.isNaN(Number(rawCount)) ? undefined : Number(rawCount);
		normalized.push({ id, name, storeCount });
	}
	return normalized;
}

function storeMatchesGroup(store: Store, groupId: string): boolean {
	const id = String(groupId);
	if (String(store.group_id ?? '') === id) return true;
	if (String(store.parent_id ?? '') === id) return true;
	const branchCodes = String(store.branch_code ?? '')
		.split(/[|,]/)
		.map((part) => part.trim())
		.filter(Boolean);
	return branchCodes.includes(id);
}

function toCreateTime(raw: Store['create_time']): number {
	if (raw == null || raw === '') return 0;
	const numeric = Number(raw);
	if (!Number.isNaN(numeric) && numeric > 0) {
		return numeric * (String(raw).length <= 10 ? 1000 : 1);
	}
	const parsed = Date.parse(String(raw).replace(' ', 'T'));
	return Number.isNaN(parsed) ? 0 : parsed;
}

function formatStoreCreateTime(raw: Store['create_time'], _locale?: string): string {
	const ts = toCreateTime(raw);
	if (ts <= 0) return raw == null || raw === '' ? '—' : String(raw);
	const date = new Date(ts);
	return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
}
